import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Row, Col, Typography, Popconfirm, InputNumber } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, YoutubeOutlined, LinkOutlined, DownloadOutlined } from '@ant-design/icons';
import api from '../utils/api';
import { downloadCsv, yearOptions } from '../utils/exportCsv';
import { hasPerm } from '../utils/perms';

const { Title, Text } = Typography;
const { Option } = Select;

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function Channels({ user }) {
  const canSeeRevenue = user?.role === 'admin' || user?.role === 'super_admin' || hasPerm(user, 'channel_revenue');
  const [channels, setChannels] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filterCompany, setFilterCompany] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [search, setSearch] = useState('');
  const [form] = Form.useForm();

  useEffect(() => { fetchCompanies(); fetchChannels(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { fetchChannels(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCompany, filterMonth, filterYear]);

  const fetchCompanies = async () => {
    try { const res = await api.get('/companies'); setCompanies(res.data); } catch (err) { console.error(err); }
  };

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterCompany) params.company_id = filterCompany;
      if (filterMonth) params.month = filterMonth;
      if (filterYear) params.year = filterYear;
      const res = await api.get('/channels', { params });
      setChannels(res.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleSave = async (values) => {
    try {
      if (editing) {
        await api.put(`/channels/${editing.id}`, values);
        message.success('Channel updated!');
      } else {
        await api.post('/channels', values);
        message.success('Channel added!');
      }
      setModalVisible(false);
      setEditing(null);
      form.resetFields();
      fetchChannels();
    } catch (err) { message.error(err.response?.data?.error || 'Failed'); }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/channels/${id}`);
      message.success('Channel deleted');
      fetchChannels();
    } catch (err) { message.error('Delete failed'); }
  };

  const columns = [
    { title: 'Channel Name', dataIndex: 'channel_name', key: 'name', render: (t) => <><YoutubeOutlined style={{ color: '#ff0000', marginRight: 8 }} />{t}</> },
    { title: 'Channel ID', dataIndex: 'channel_id', key: 'cid', render: (v) => v || '-' },
    {
      title: 'URL', dataIndex: 'channel_url', key: 'url',
      render: (v) => v ? <a href={v} target="_blank" rel="noreferrer"><LinkOutlined /> Open</a> : '-'
    },
    { title: 'Subscribers', dataIndex: 'subscriber_count', key: 'subs', render: (v) => (v || 0).toLocaleString('en-IN') },
    ...(canSeeRevenue ? [
      {
        title: 'Revenue ($)', dataIndex: 'revenue_usd', key: 'rev_usd',
        render: (v) => <Text style={{ color: '#1677ff' }}>${Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text>,
        sorter: (a, b) => (Number(a.revenue_usd) || 0) - (Number(b.revenue_usd) || 0)
      },
    ] : []),
    { title: 'Company', dataIndex: 'company_name', key: 'company' },
    { title: 'Vendor', dataIndex: 'vendor', key: 'vendor', render: (v) => v || '-' },
    {
      title: 'Actions', key: 'actions', width: 100,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => { setEditing(r); form.setFieldsValue(r); setModalVisible(true); }} />
          <Popconfirm title="Delete?" onConfirm={() => handleDelete(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={3} style={{ margin: 0 }}><YoutubeOutlined style={{ color: '#ff0000' }} /> Bainsla Music Channels</Title></Col>
        <Col>
          <Space>
            <Input.Search placeholder="Search channel name" allowClear value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 200 }} />
            <Select placeholder="Filter by Company" value={filterCompany || undefined} onChange={(v) => setFilterCompany(v || '')} allowClear style={{ width: 180 }}>
              {companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}
            </Select>
            <Select placeholder="All months" value={filterMonth || undefined} onChange={(v) => setFilterMonth(v || '')} allowClear style={{ width: 130 }}>
              {monthNames.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
            </Select>
            <Select placeholder="All years" value={filterYear || undefined} onChange={(v) => setFilterYear(v || '')} allowClear style={{ width: 110 }}>
              {yearOptions.map(y => <Option key={y} value={y}>{y}</Option>)}
            </Select>
            {canSeeRevenue && <Button icon={<DownloadOutlined />} onClick={() => downloadCsv('/channels/export', { company_id: filterCompany, month: filterMonth, year: filterYear })}>Download Excel</Button>}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditing(null); form.resetFields(); setModalVisible(true); }}>Add Channel</Button>
          </Space>
        </Col>
      </Row>

      <Table dataSource={search.trim() ? channels.filter(c => (c.channel_name || '').toLowerCase().includes(search.trim().toLowerCase())) : channels} columns={columns} rowKey="id" loading={loading}
        summary={canSeeRevenue ? (data) => {
          const totUsd = data.reduce((s, r) => s + (Number(r.revenue_usd) || 0), 0);
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={4}><Text strong>TOTAL REVENUE</Text></Table.Summary.Cell>
              <Table.Summary.Cell index={4}><Text strong style={{ color: '#1677ff' }}>${totUsd.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Text></Table.Summary.Cell>
              <Table.Summary.Cell index={5} colSpan={3} />
            </Table.Summary.Row>
          );
        } : undefined}
      />

      <Modal title={editing ? 'Edit Channel' : 'Add Channel'} open={modalVisible}
        onCancel={() => { setModalVisible(false); setEditing(null); }} onOk={() => form.submit()}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="channel_name" label="Channel Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Form.Item name="channel_id" label="Channel ID"><Input placeholder="UC..." /></Form.Item>
          <Form.Item name="channel_url" label="Channel URL"><Input placeholder="https://youtube.com/@..." /></Form.Item>
          <Form.Item name="subscriber_count" label="Subscribers"><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="vendor" label="Vendor (kiske paas channel / payment kisko)"><Input placeholder="Vendor / channel holder ka naam" /></Form.Item>
          <Form.Item name="company_id" label="Company" rules={[{ required: true }]}>
            <Select>{companies.map(c => <Option key={c.id} value={c.id}>{c.name}</Option>)}</Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
